import { faker } from '@faker-js/faker';

const DEFAULT_LIMIT = 20;

export const genMockResponse = async (
	options: Api.Mocker.Options<any, any>,
	fields?: readonly string[],
	params?: Api.Mocker.Params,
	requestInitSignal?: ReturnType<Api.ClientRequest<any>['abortByFingerprint']>,
) => {
	if (options.delay) {
		if (options.showLog) {
			console.log('genMock pause', options.delay);
		}

		const { sleep } = await import('@/core/utils/system');

		let promise = sleep(options.delay);

		if (requestInitSignal) {
			promise = Promise.race<void>([
				promise,

				// обработчик отмены
				new Promise((_resolve, reject) => {
					requestInitSignal.addEventListener('abort', () => {
						reject(new DOMException('The operation was aborted from API mocker.', 'AbortError'));
					});
				}),
			]);
		}

		await promise;
	}

	const res = {
		result: genMockResult(options, fields, params),
		errors: undefined,
		nextOffset: undefined as number | undefined,
		total: undefined as number | undefined,
	};

	delete res.errors;
	delete res.nextOffset;
	delete res.total;

	// реализация `limit` и `offset`
	if (Array.isArray(res.result) && options.total && params && params.limit) {
		if (options.useFilter) {
			res.result = res.result.filter((resultItem) => options.useFilter?.(resultItem, params));
		}

		const offset = params.offset ?? 0;

		if (res.result.length > params.limit + offset) {
			res.nextOffset = params.limit + offset;
			res.total = options.total;
		}

		res.result = res.result.slice(offset, offset + params.limit);
	}

	if (options.showLog) {
		console.log('genMock complete', res);
	}

	return res;
};

export const genMockResult = <Options extends Api.Mocker.Options<any, any>>(
	options: Options,
	fields?: readonly string[],
	params?: Api.Mocker.Params,
): Api.Mocker.ToMockResult<Options['result']> => {
	let seed: number | undefined = options.seed ?? 0;
	if (seed === -1) seed = Math.random();

	if (options.result instanceof Function) {
		faker.seed(seed);

		return genMockValue(options.result, 0, fields, params);
	}

	if (Array.isArray(options.result)) {
		const genItem = options.result[0];

		const result = [] as Api.Mocker.ToMockResult<Options['result']>;

		// для работы `useFilter()` применять `params.limit` можно только после применения фильтра
		const total = options.total ?? params?.limit ?? DEFAULT_LIMIT;

		for (let i = 0; i < total; i++) {
			result.push(genMockItem(genItem, i, seed, fields, params));
		}

		return result;
	}

	return genMockItem(options.result, 0, seed, fields, params) as Api.Mocker.ToMockResult<Options['result']>;
};

export const genMockItem = <T extends Api.Mocker.Item>(
	genItem: T,
	n: number,
	seed: number,
	fields?: readonly string[],
	params?: Api.Mocker.Params,
): Api.Mocker.ToMockItem<T> => {
	const item = {} as Api.Mocker.ToMockItem<T>;

	for (const key in genItem) {
		const genValue = genItem[key];

		if (fields && !fields.includes(key)) continue;

		// seed для генерации одного объекта всегда одинаковый
		faker.seed(seed + n);

		item[key] = genMockValue(genValue, n, fields, params);
	}

	return item;
};

export const genMockValue = <T extends Api.Mocker.Value>(
	genValue: T,
	n: number = 0,
	fields?: readonly string[],
	params?: Api.Mocker.Params,
): Api.Mocker.ToMockValue<T> => {
	return genValue(n, fields, params);
};
